import React from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { useTenant } from '../../contexts/TenantContext';
import { Logo } from './Logo';

interface TenantBrandMarkProps {
  logoSize?: number;
  isDarkMode?: boolean;
  className?: string;
}

/**
 * Two-line composite brand mark for auth screens.
 *
 *   Platform (no tenant):           Tenant subdomain (IITM):
 *   ┌─────────────┐                 ┌─────────────────────┐
 *   │  [Logo 44]  │                 │     [Logo 44]       │
 *   │  vidya.ai   │                 │       IITM          │
 *   │             │                 │ powered by Vidya.ai │
 *   └─────────────┘                 └─────────────────────┘
 *
 * On platform the "powered by" line is suppressed — Vidya is the only brand.
 */
export const TenantBrandMark: React.FC<TenantBrandMarkProps> = ({
  logoSize = 44,
  isDarkMode: isDarkModeProp,
  className = '',
}) => {
  const theme = useTheme();
  const { tenant } = useTenant();
  const isDarkMode = isDarkModeProp ?? theme.isDarkMode;

  const headingText = tenant?.name ?? 'vidya.ai';

  return (
    <div className={`flex flex-col items-center ${className}`}>
      <div className="flex items-center space-x-3">
        <Logo variant="app-icon" size={logoSize} isDarkMode={isDarkMode} />
        <h1
          className={`text-3xl font-bold leading-tight ${
            isDarkMode ? 'text-white' : 'text-gray-900'
          }`}
        >
          {headingText}
        </h1>
      </div>
      {tenant && (
        <p
          className={`mt-1 text-xs tracking-wide ${
            isDarkMode ? 'text-slate-400' : 'text-gray-500'
          }`}
        >
          powered by <span className="font-medium">Vidya.ai</span>
        </p>
      )}
    </div>
  );
};

export default TenantBrandMark;
